import {
  COGNITO_APP_CLIENT_ID,
  COGNITO_DOMAIN,
  COGNITO_REDIRECT_SIGN_IN,
  COGNITO_REDIRECT_SIGN_OUT,
  COGNITO_REGION,
  COGNITO_USER_POOL_ID,
} from '../config/endpoints.js';

let isAmplifyConfigured = false;
let pendingSignInChallenge = null;

const ensureCognitoConfig = () => {
  if (!COGNITO_REGION || !COGNITO_USER_POOL_ID || !COGNITO_APP_CLIENT_ID) {
    throw new Error('Configuração Cognito incompleta no frontend');
  }
};

const configureAmplifyIfNeeded = async () => {
  if (isAmplifyConfigured) {
    return;
  }

  ensureCognitoConfig();
  const { Amplify } = await import('aws-amplify');

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: COGNITO_USER_POOL_ID,
        userPoolClientId: COGNITO_APP_CLIENT_ID,
        loginWith: {
          username: true,
          oauth:
            COGNITO_DOMAIN && COGNITO_REDIRECT_SIGN_IN && COGNITO_REDIRECT_SIGN_OUT
              ? {
                  domain: COGNITO_DOMAIN,
                  scopes: ['openid', 'email', 'profile'],
                  redirectSignIn: [COGNITO_REDIRECT_SIGN_IN],
                  redirectSignOut: [COGNITO_REDIRECT_SIGN_OUT],
                  responseType: 'code',
                }
              : undefined,
        },
      },
    },
  });

  isAmplifyConfigured = true;
};

const loginWithCognito = async ({ username, password }) => {
  await configureAmplifyIfNeeded();
  const { signIn, fetchAuthSession } = await import('aws-amplify/auth');

  const signInResult = await signIn({
    username,
    password,
    options: { authFlowType: 'USER_PASSWORD_AUTH' },
  });

  if (signInResult?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    pendingSignInChallenge = signInResult.nextStep;
    return {
      challengeName: 'FORCE_CHANGE_PASSWORD',
      challengeStep: signInResult.nextStep.signInStep,
    };
  }

  if (signInResult?.nextStep?.signInStep !== 'DONE') {
    throw new Error('Fluxo Cognito não concluído');
  }

  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();
  const idToken = session.tokens?.idToken?.toString();

  if (!accessToken) {
    throw new Error('Sessão Cognito sem access token');
  }

  return { accessToken, idToken };
};

const completeNewPasswordChallenge = async ({ newPassword }) => {
  await configureAmplifyIfNeeded();
  const { confirmSignIn, fetchAuthSession } = await import('aws-amplify/auth');

  if (!pendingSignInChallenge) {
    throw new Error('Fluxo de troca de senha expirado. Faca login novamente.');
  }

  const result = await confirmSignIn({
    challengeResponse: newPassword,
  });

  if (result?.nextStep?.signInStep !== 'DONE') {
    throw new Error('Nao foi possivel concluir a troca de senha.');
  }

  pendingSignInChallenge = null;

  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();
  const idToken = session.tokens?.idToken?.toString();

  if (!accessToken) {
    throw new Error('Sessao Cognito sem access token');
  }

  return { accessToken, idToken };
};

const getCognitoSessionTokens = async () => {
  await configureAmplifyIfNeeded();
  const { fetchAuthSession } = await import('aws-amplify/auth');
  const session = await fetchAuthSession();

  const accessToken = session.tokens?.accessToken?.toString() || null;
  const idToken = session.tokens?.idToken?.toString() || null;
  return { accessToken, idToken };
};

const requestPasswordReset = async ({ username }) => {
  await configureAmplifyIfNeeded();
  const { resetPassword } = await import('aws-amplify/auth');

  const result = await resetPassword({ username });
  return {
    nextStep: result?.nextStep?.resetPasswordStep || 'UNKNOWN',
    deliveryMedium: result?.nextStep?.codeDeliveryDetails?.deliveryMedium || null,
    destination: result?.nextStep?.codeDeliveryDetails?.destination || null,
  };
};

const confirmPasswordReset = async ({ username, confirmationCode, newPassword }) => {
  await configureAmplifyIfNeeded();
  const { confirmResetPassword } = await import('aws-amplify/auth');
  await confirmResetPassword({
    username,
    confirmationCode,
    newPassword,
  });
};

const logoutCognito = async () => {
  await configureAmplifyIfNeeded();
  const { signOut } = await import('aws-amplify/auth');
  await signOut();
  pendingSignInChallenge = null;
};

const __resetCognitoAuthStateForTests = () => {
  pendingSignInChallenge = null;
  isAmplifyConfigured = false;
};

export {
  loginWithCognito,
  completeNewPasswordChallenge,
  requestPasswordReset,
  confirmPasswordReset,
  getCognitoSessionTokens,
  logoutCognito,
  configureAmplifyIfNeeded,
  __resetCognitoAuthStateForTests,
};
